import type {
  CSSProperties,
  DragEventHandler,
  EventHandler,
  HTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  SyntheticEvent,
} from 'react'
import React from 'react'
import { Icon, Spinner, TooltipPopOver } from '@ztechapps/webui.util'
import { Button } from 'react-bootstrap'
import type { ButtonVariant } from 'react-bootstrap/types'
import type { OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger'
import styles from './BusyButton.module.scss'

// Width of a blocked button's reason popover — an explicit `width` replaces the 276px plain-text
// cap (libs/webui/util/src/TooltipPopOver/index.tsx:14-19), following the `320` preview width in
// libs/webui/util/src/EntityCodeLink/index.tsx:9.
const BLOCKED_REASON_WIDTH = 320

export type BusyButtonProps = {
  isBusy?: boolean
  disabled?: boolean
  disabledReason?: string
  bsStyle?: string
  variant?: ButtonVariant
  bsSize?: 'sm' | 'lg'
  className?: string
  type?: 'button' | 'submit' | 'reset'
  onClick?: EventHandler<SyntheticEvent> | MouseEventHandler<HTMLButtonElement>
  icon?: string | ReactNode
  text?: string
  busyText?: string
  title?: string
  name?: string
  active?: boolean
  'aria-label'?: string
  children?: ReactNode
  style?: CSSProperties | undefined
  ref?: any
  // Native HTML5 DnD passthrough — required so this button can serve as a
  // `useDragSort` handle. `draggable=true` placed on any parent does NOT
  // initiate a drag from inside a `<button>`; the button element itself
  // must carry `draggable` and own the drag-source handlers.
  // `draggable` is typed as the React `Booleanish` union (`boolean | 'true' | 'false'`)
  // so callers forwarding `ButtonProps` from react-bootstrap can spread cleanly.
  draggable?: boolean | 'true' | 'false'
  onDragStart?: DragEventHandler<HTMLButtonElement>
  onDragEnd?: DragEventHandler<HTMLButtonElement>
  'data-drag-sort-index'?: number
  /**
   * Fullscreen portal target for the reason/title tooltip — stays undefined
   * off-fullscreen so the overlay keeps its default document.body portal.
   */
  container?: OverlayTriggerProps['container']
} & Pick<
  HTMLAttributes<HTMLButtonElement>,
  'onMouseEnter' | 'onMouseLeave' | 'onMouseOver' | 'onMouseOut' | 'onFocus' | 'onBlur' | 'aria-expanded'
>

/**
 *  The common button that handles busy state
 */
export const BusyButton = ({
  children,
  isBusy = false,
  disabled = false,
  disabledReason,
  bsStyle = 'primary',
  variant,
  bsSize,
  className = '',
  type = 'button',
  onClick,
  icon,
  text,
  busyText = 'Loading...',
  title,
  active = false,
  style = undefined,
  ref,
  draggable,
  onDragStart,
  onDragEnd,
  // The handlers a wrapping `TooltipPopOver` injects: the DOM button must hear them for that popover to open.
  onMouseEnter,
  onMouseLeave,
  onMouseOver,
  onMouseOut,
  onFocus,
  onBlur,
  'data-drag-sort-index': dataDragSortIndex,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  container,
}: BusyButtonProps) => {
  //check the value from the variant first, then try to use teh bsStyle
  bsStyle = variant ?? bsStyle

  //Update this to handle custom icons
  let renderIcon = null
  if (icon) {
    // Glyph is decorative — the visible text or aria-label carries the accessible name.
    const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon
    renderIcon = <span aria-hidden="true">{iconNode}</span>
  }

  const blocked = Boolean(disabledReason)
  // A blocked button shows why it is blocked, any other button its title. An icon-only button has
  // no text to name it, so its title is also its accessible name.
  const isIconOnly = Boolean(icon) && !text && !children
  const tooltipBody = blocked ? disabledReason : title
  const resolvedAriaLabel = ariaLabel ?? (isIconOnly ? title : undefined)
  // No body, no popover: a bare button hands the handlers a wrapping `TooltipPopOver` injects straight to the DOM.
  const withTooltip = (button: ReactElement) =>
    tooltipBody ? (
      <TooltipPopOver
        tooltipBody={tooltipBody}
        width={blocked ? BLOCKED_REASON_WIDTH : undefined}
        container={container}
      >
        {button}
      </TooltipPopOver>
    ) : (
      button
    )

  let renderText = null
  if (text) {
    //if busy and there is a value for busy text
    if (isBusy && busyText) {
      text = busyText
    }
    renderText = text
  }

  let buttonSpinner = null
  if (isBusy) {
    buttonSpinner = <Spinner size={'sm'} />
  }

  if (isBusy) {
    if (blocked) {
      className = `${className} disabled pe-auto ${styles.blocked}`
    }
    return withTooltip(
      <Button
        ref={ref}
        size={bsSize}
        variant={bsStyle}
        className={className}
        type={type}
        aria-disabled={blocked || undefined}
        disabled={blocked ? undefined : true}
        style={style}
        aria-label={resolvedAriaLabel}
        aria-expanded={ariaExpanded}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {buttonSpinner} {busyText}
      </Button>
    )
  }
  // A blocked button keeps the disabled look, swallows clicks and stays
  // focusable: the native attribute would drop it from the tab order.
  // Bootstrap's disabled rule drops pointer events too, so pe-auto below
  // restores them and the reason popover shows on hover. Restoring pointer
  // events also re-enables Bootstrap's :active press styling and its
  // .btn { cursor: pointer } rule. Hover colour depends on the variant:
  // `.btn:hover` and `.btn.disabled` tie at (0,2,0) and `.btn.disabled`
  // wins for the primary variant, but `.btn-link:hover` is declared after
  // `.btn.disabled`, so the link variant needs the module pin. The `blocked`
  // module class (matching or higher specificity than Bootstrap's own
  // rules) pins the disabled look back on for press, cursor and link hover
  // alike. No wrapper element, which would break .btn-group's direct-child
  // joins.
  if (disabled || isBusy || blocked) {
    className = `${className} disabled`
    onClick = () => {
      //empty here
    }
  }
  if (blocked) {
    className = `${className} pe-auto ${styles.blocked}`
  }

  return withTooltip(
    <Button
      ref={ref}
      aria-label={resolvedAriaLabel}
      aria-expanded={ariaExpanded}
      aria-disabled={blocked || undefined}
      size={bsSize}
      variant={bsStyle}
      className={className}
      type={type}
      onClick={onClick}
      disabled={blocked ? undefined : disabled || isBusy}
      active={active}
      style={style}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onFocus={onFocus}
      onBlur={onBlur}
      data-drag-sort-index={dataDragSortIndex}
    >
      {renderIcon} {renderText} {children}
    </Button>
  )
}
